import { useCharacterList } from '../../domains/characters/queries'
import styles from './assistant.module.css'

export interface CharacterContextPickerProps {
  /** The attached character, or null for none. */
  value: number | null
  busy: boolean
  onChange: (characterId: number | null) => void
}

/**
 * Attaches one of the caller's characters to the conversation, or none.
 *
 * A native `<select>` rather than a designed dropdown: it is one choice from a list the user
 * already knows, it is keyboard- and screen-reader-correct without any work, and on a phone it
 * opens the platform's own picker — which beats a custom menu inside a drawer that is itself
 * already an overlay.
 *
 * The roster is the caller's own, which is also the rule the API enforces on attach: Bob is
 * given the whole sheet unfiltered, so a character somebody else owns is not offered here and
 * would be refused if it were.
 */
export function CharacterContextPicker({ value, busy, onChange }: CharacterContextPickerProps) {
  // One page of 50. The roster screen pages properly because a roster can be long; this is a
  // context picker, and a player with more than fifty characters can still reach any of them
  // from the sheet's own "Ask Bob" button.
  const roster = useCharacterList(1, 50)

  return (
    <label className={styles.picker}>
      <span className={styles.pickerLabel}>Character context</span>
      <select
        className={styles.pickerSelect}
        value={value ?? ''}
        disabled={busy || roster.isPending}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      >
        <option value="">No character</option>
        {(roster.data?.items ?? []).map((character) => (
          <option key={character.id} value={character.id}>
            {character.name}
          </option>
        ))}
      </select>
    </label>
  )
}
